import { ref, computed, reactive } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '../services/api';

/* ────────────────────────────────────────────────────────────────
 * Types
 * ──────────────────────────────────────────────────────────────── */

export interface AiProvider {
  slug: string;
  name: string;
  provider?: string;
  model?: string;
  isDefault?: boolean;
  is_default?: boolean;
  supportsTools?: boolean;
  supports_tools?: boolean;
}

export type AiEntityType =
  | 'project'
  | 'task'
  | 'milestone'
  | 'document'
  | 'spreadsheet'
  | 'file'
  | 'folder'
  | 'employee'
  | 'department'
  | 'designation'
  | 'client'
  | 'lead'
  | 'ticket'
  | 'invoice'
  | 'expense'
  | 'estimate'
  | 'contract'
  | 'leave'
  | 'event'
  | 'article'
  | 'notice';

export type AiToolKind = 'read' | 'write';

export interface AiEntity {
  type: AiEntityType;
  id: number | string;
  name: string;
  url?: string;
}

export interface AiToolCall {
  id: string;
  name: string;
  /** Read tools only look things up; write tools change the workspace. */
  kind?: AiToolKind;
  input?: unknown;
  ok?: boolean;
  summary?: string;
  error?: string;
  durationMs?: number;
  status: 'running' | 'done' | 'error';
  entities?: AiEntity[];
}

export interface AiUsage {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  costCredits?: number;
  model?: string;
  provider?: string;
  steps?: number;
  durationMs?: number;
}

export interface AiMessageMetadata {
  toolCalls?: AiToolCall[];
  entities?: AiEntity[];
  usage?: AiUsage;
  model?: string;
  provider?: string;
  durationMs?: number;
  partial?: boolean;
  [key: string]: unknown;
}

export interface AiMessage {
  id: number | string;
  conversation_id?: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: AiMessageMetadata | null;
  input_tokens?: number;
  output_tokens?: number;
  created_at?: string;
}

export interface AiConversation {
  id: number;
  title: string;
  type?: string;
  pinned?: boolean;
  is_pinned?: boolean;
  is_archived?: boolean;
  last_message_preview?: string | null;
  total_tokens?: number;
  ai_provider_id?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface AiCredits {
  total_credits?: number;
  used_credits?: number;
  credit_balance?: number;
  monthly_allowance?: number;
  period_started_at?: string | null;
  is_unlimited?: boolean;
  remaining_credits?: number;
}

export interface AiUsageLimits {
  maxMessagesPerDay: number;
  messagesUsedToday: number;
  maxTokensPerRequest: number;
}

export interface AiUsageDay {
  date: string;
  tokens: number;
  cost: number;
  messages: number;
}

export interface AiMyUsage {
  limits?: AiUsageLimits | null;
  today?: { messages: number; tokens: number; cost: number };
  last7Days?: AiUsageDay[];
  byModel?: { model: string; tokens: number; cost: number }[];
  conversationsCount?: number;
}

export interface AiConversationUsage {
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  costCredits: number;
  messages: number;
  byMessage?: unknown[];
}

export interface StreamState {
  active: boolean;
  content: string;
  toolCalls: AiToolCall[];
  entities: AiEntity[];
  usage: AiUsage | null;
  error: string | null;
  startedAt: number;
}

type SseEvent =
  | { type: 'user_message'; message?: AiMessage; data?: AiMessage }
  | { type: 'chunk'; content: string }
  | { type: 'tool_start'; toolCallId: string; name: string; input?: unknown; kind?: AiToolKind }
  | { type: 'tool_result'; toolCallId: string; name: string; ok: boolean; summary?: string; entities?: AiEntity[]; error?: string; durationMs?: number }
  | { type: 'usage' } & AiUsage
  | { type: 'assistant_message'; message?: AiMessage; data?: AiMessage }
  | { type: 'error'; message?: string; code?: string }
  | { type: string; [key: string]: unknown };

/* ────────────────────────────────────────────────────────────────
 * Shared state (module-level so every component sees the same data)
 * ──────────────────────────────────────────────────────────────── */

const loading = ref(true);
const conversations = ref<AiConversation[]>([]);
const conversationsLoading = ref(false);
const showArchived = ref(false);
const searchQuery = ref('');
const activeConversation = ref<AiConversation | null>(null);
const messages = ref<AiMessage[]>([]);
const messagesLoading = ref(false);
const providers = ref<AiProvider[]>([]);
const selectedProvider = ref('');
const credits = ref<AiCredits | null>(null);
const myUsage = ref<AiMyUsage | null>(null);
const conversationUsage = ref<AiConversationUsage | null>(null);
const errorMessage = ref('');
const lastFailedPrompt = ref('');
const stream = reactive<StreamState>({
  active: false,
  content: '',
  toolCalls: [],
  entities: [],
  usage: null,
  error: null,
  startedAt: 0,
});

let abortController: AbortController | null = null;

/**
 * Forget the signed-in user's conversations, messages, usage and credits.
 * Called by stores/sessionScope.ts on sign-out and when another user signs in,
 * because this state is module-level and would otherwise outlive the session.
 */
export function resetAiAgentState(): void {
  abortController?.abort();
  abortController = null;
  loading.value = true;
  conversations.value = [];
  conversationsLoading.value = false;
  showArchived.value = false;
  searchQuery.value = '';
  activeConversation.value = null;
  messages.value = [];
  messagesLoading.value = false;
  providers.value = [];
  selectedProvider.value = '';
  credits.value = null;
  myUsage.value = null;
  conversationUsage.value = null;
  errorMessage.value = '';
  lastFailedPrompt.value = '';
  Object.assign(stream, { active: false, content: '', toolCalls: [], entities: [], usage: null, error: null, startedAt: 0 });
}

/* ────────────────────────────────────────────────────────────────
 * Derived
 * ──────────────────────────────────────────────────────────────── */

const usageLimits = computed<AiUsageLimits | null>(() => myUsage.value?.limits ?? null);

const creditBalance = computed(() => {
  if (!credits.value) return 0;
  return Number(credits.value.credit_balance ?? credits.value.remaining_credits ?? 0);
});
const totalCredits = computed(() => Number(credits.value?.total_credits ?? 0));
// Only an explicit superadmin "unlimited" flag lifts the cap; every company
// otherwise has a balance (seeded from its package's monthly allowance).
const hasCreditCap = computed(() => !!credits.value && !credits.value.is_unlimited);
const creditPercent = computed(() =>
  hasCreditCap.value ? Math.min(100, Math.max(0, (creditBalance.value / totalCredits.value) * 100)) : 100,
);
const hasDailyLimit = computed(() => (usageLimits.value?.maxMessagesPerDay ?? 0) > 0);
const messagesLeftToday = computed(() => {
  if (!hasDailyLimit.value || !usageLimits.value) return 0;
  return Math.max(0, usageLimits.value.maxMessagesPerDay - usageLimits.value.messagesUsedToday);
});

const currentProvider = computed(() => providers.value.find((p) => p.slug === selectedProvider.value) ?? null);

const filteredConversations = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return conversations.value.filter((c) => {
    if (!q) return true;
    return (c.title || '').toLowerCase().includes(q) || (c.last_message_preview || '').toLowerCase().includes(q);
  });
});

const pinnedConversations = computed(() => filteredConversations.value.filter(isPinned));
const unpinnedConversations = computed(() => filteredConversations.value.filter((c) => !isPinned(c)));

export function isPinned(c: AiConversation): boolean {
  return !!(c.pinned ?? c.is_pinned);
}

/* ────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────── */

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string };
  return e?.response?.data?.error?.message || e?.response?.data?.message || fallback;
}

function resetStream(): void {
  stream.active = false;
  stream.content = '';
  stream.toolCalls = [];
  stream.entities = [];
  stream.usage = null;
  stream.error = null;
  stream.startedAt = 0;
}

function upsertConversation(conv: AiConversation): void {
  const idx = conversations.value.findIndex((c) => c.id === conv.id);
  if (idx === -1) conversations.value.unshift(conv);
  else conversations.value[idx] = { ...conversations.value[idx], ...conv };
}

/**
 * Parse one SSE frame payload. Returns null for `[DONE]` or malformed JSON.
 */
export function parseSseData(raw: string): SseEvent | null | 'done' {
  const data = raw.trim();
  if (!data) return null;
  if (data === '[DONE]') return 'done';
  try {
    return JSON.parse(data) as SseEvent;
  } catch {
    return null;
  }
}

/**
 * Feed a decoded text chunk into the line buffer and yield complete `data:` payloads.
 * Robust to partial frames (remainder is kept), multiple events per chunk, CRLF,
 * and multi-line `data:` frames (joined with "\n" per the SSE spec).
 */
export function createSseLineParser(): { push: (text: string) => string[]; flush: () => string[] } {
  let buffer = '';
  let pendingData: string[] = [];

  function collect(lines: string[]): string[] {
    const payloads: string[] = [];
    for (const rawLine of lines) {
      const line = rawLine.replace(/\r$/, '');
      if (line === '') {
        if (pendingData.length) {
          payloads.push(pendingData.join('\n'));
          pendingData = [];
        }
        continue;
      }
      if (line.startsWith(':')) continue; // comment / keep-alive
      if (line.startsWith('data:')) {
        pendingData.push(line.slice(5).replace(/^ /, ''));
      }
      // `event:` / `id:` / `retry:` fields are ignored; every event is typed in JSON.
    }
    return payloads;
  }

  return {
    push(text: string) {
      buffer += text;
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      return collect(lines);
    },
    flush() {
      const rest = buffer;
      buffer = '';
      const payloads = collect(rest ? [rest, ''] : ['']);
      return payloads;
    },
  };
}

/* ────────────────────────────────────────────────────────────────
 * Composable
 * ──────────────────────────────────────────────────────────────── */

export function useAiAgent() {
  const router = useRouter();
  const route = useRoute();

  /* ---------- Loading ---------- */

  async function loadConversations(): Promise<void> {
    conversationsLoading.value = true;
    try {
      const params: Record<string, string | number> = { limit: 100 };
      if (showArchived.value) params.archived = 1;
      const res = await api.get('/ai/conversations', { params });
      const list = res.data?.data;
      conversations.value = Array.isArray(list) ? list : [];
    } catch {
      conversations.value = [];
    } finally {
      conversationsLoading.value = false;
    }
  }

  async function loadProviders(): Promise<void> {
    try {
      const res = await api.get('/ai/providers');
      providers.value = Array.isArray(res.data?.data) ? res.data.data : [];
      if (!selectedProvider.value || !providers.value.some((p) => p.slug === selectedProvider.value)) {
        const def = providers.value.find((p) => p.isDefault ?? p.is_default);
        selectedProvider.value = def?.slug || providers.value[0]?.slug || '';
      }
    } catch {
      providers.value = [];
    }
  }

  async function loadCredits(): Promise<void> {
    try {
      const res = await api.get('/ai/credits');
      credits.value = res.data?.data ?? null;
    } catch {
      credits.value = null;
    }
  }

  async function loadMyUsage(): Promise<void> {
    try {
      const res = await api.get('/ai/usage/me');
      myUsage.value = res.data?.data ?? null;
    } catch {
      myUsage.value = null;
    }
  }

  async function loadConversationUsage(id: number): Promise<void> {
    try {
      const res = await api.get(`/ai/conversations/${id}/usage`);
      conversationUsage.value = res.data?.data ?? null;
    } catch {
      conversationUsage.value = null;
    }
  }

  async function loadAll(): Promise<void> {
    loading.value = true;
    await Promise.all([loadConversations(), loadProviders(), loadCredits(), loadMyUsage()]);
    loading.value = false;
  }

  function refreshMeters(): void {
    void loadCredits();
    void loadMyUsage();
  }

  /* ---------- URL persistence ---------- */

  function syncUrl(conversationId: number | null): void {
    const query = { ...route.query } as Record<string, string | string[] | null | undefined>;
    if (conversationId) query.c = String(conversationId);
    else delete query.c;
    void router.replace({ query });
  }

  function routeConversationId(): number | null {
    const raw = route.query.c;
    const val = Array.isArray(raw) ? raw[0] : raw;
    const n = Number(val);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /* ---------- Conversations ---------- */

  // Each openConversation() call takes a ticket; a response only lands if its
  // ticket is still the latest, so clicking A then B never shows A's messages
  // under B's title when A's request finishes last.
  let openSeq = 0;
  async function openConversation(conv: AiConversation | number): Promise<void> {
    const id = typeof conv === 'number' ? conv : conv.id;
    const ticket = ++openSeq;
    if (stream.active) stopStreaming();
    activeConversation.value =
      typeof conv === 'number' ? conversations.value.find((c) => c.id === id) ?? { id, title: '' } : conv;
    messages.value = [];
    conversationUsage.value = null;
    errorMessage.value = '';
    syncUrl(id);
    messagesLoading.value = true;
    try {
      const res = await api.get(`/ai/conversations/${id}`);
      if (ticket !== openSeq) return;
      const data = res.data?.data ?? {};
      const list: AiMessage[] = Array.isArray(data.messages) ? data.messages : [];
      messages.value = list.map(normalizeMessage);
      const { messages: _m, ...rest } = data;
      activeConversation.value = { ...(activeConversation.value ?? {}), ...rest } as AiConversation;
      void loadConversationUsage(id);
    } catch (err) {
      if (ticket !== openSeq) return;
      errorMessage.value = extractError(err, '');
      if (!errorMessage.value) errorMessage.value = 'error';
      activeConversation.value = null;
      syncUrl(null);
    } finally {
      if (ticket === openSeq) messagesLoading.value = false;
    }
  }

  function normalizeMessage(m: AiMessage): AiMessage {
    let metadata = m.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata as unknown as string);
      } catch {
        metadata = null;
      }
    }
    const toolCalls = Array.isArray(metadata?.toolCalls)
      ? metadata!.toolCalls!.map((tc) => ({
          ...tc,
          status: tc.status ?? (tc.ok === false ? 'error' : 'done'),
        }))
      : undefined;
    return { ...m, metadata: metadata ? { ...metadata, toolCalls } : metadata };
  }

  function goHome(): void {
    if (stream.active) stopStreaming();
    activeConversation.value = null;
    messages.value = [];
    conversationUsage.value = null;
    errorMessage.value = '';
    syncUrl(null);
  }

  async function createConversation(type = 'chat', title?: string): Promise<AiConversation | null> {
    try {
      const res = await api.post('/ai/conversations', {
        type,
        title,
        providerSlug: selectedProvider.value || undefined,
      });
      const conv: AiConversation = res.data.data;
      conversations.value.unshift(conv);
      activeConversation.value = conv;
      messages.value = [];
      conversationUsage.value = null;
      errorMessage.value = '';
      syncUrl(conv.id);
      return conv;
    } catch (err) {
      errorMessage.value = extractError(err, 'error');
      return null;
    }
  }

  async function renameConversation(conv: AiConversation, title: string): Promise<boolean> {
    const clean = title.trim();
    if (!clean) return false;
    try {
      await api.patch(`/ai/conversations/${conv.id}`, { title: clean });
      conv.title = clean;
      upsertConversation({ ...conv, title: clean });
      if (activeConversation.value?.id === conv.id) activeConversation.value.title = clean;
      return true;
    } catch (err) {
      errorMessage.value = extractError(err, 'error');
      return false;
    }
  }

  async function togglePin(conv: AiConversation): Promise<void> {
    const next = !isPinned(conv);
    try {
      const res = await api.patch(`/ai/conversations/${conv.id}/pin`, { pinned: next });
      const updated = res.data?.data;
      const merged: AiConversation = { ...conv, ...(updated || {}), pinned: updated?.pinned ?? updated?.is_pinned ?? next };
      upsertConversation(merged);
      if (activeConversation.value?.id === conv.id) activeConversation.value = { ...activeConversation.value, ...merged };
    } catch (err) {
      errorMessage.value = extractError(err, 'error');
    }
  }

  async function toggleArchive(conv: AiConversation): Promise<void> {
    try {
      await api.patch(`/ai/conversations/${conv.id}/archive`);
      await loadConversations();
      if (activeConversation.value?.id === conv.id) goHome();
    } catch (err) {
      errorMessage.value = extractError(err, 'error');
    }
  }

  async function deleteConversation(conv: AiConversation): Promise<void> {
    try {
      await api.delete(`/ai/conversations/${conv.id}`);
      conversations.value = conversations.value.filter((c) => c.id !== conv.id);
      if (activeConversation.value?.id === conv.id) goHome();
    } catch (err) {
      errorMessage.value = extractError(err, 'error');
    }
  }

  /* ---------- Streaming ---------- */

  function stopStreaming(): void {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  function applyEvent(ev: SseEvent, tempUserId: string | number): { assistant?: AiMessage } {
    switch (ev.type) {
      case 'user_message': {
        const real = (ev as { message?: AiMessage; data?: AiMessage }).message ?? (ev as { data?: AiMessage }).data;
        if (real) {
          const idx = messages.value.findIndex((m) => m.id === tempUserId);
          if (idx !== -1) messages.value[idx] = normalizeMessage(real);
        }
        return {};
      }
      case 'chunk': {
        const content = (ev as { content?: unknown }).content;
        if (typeof content === 'string') stream.content += content;
        return {};
      }
      case 'tool_start': {
        const e = ev as { toolCallId?: string; name?: string; input?: unknown; kind?: AiToolKind };
        const id = e.toolCallId || `${e.name}-${stream.toolCalls.length}`;
        stream.toolCalls.push({ id, name: e.name || 'tool', kind: e.kind, input: e.input, status: 'running' });
        return {};
      }
      case 'tool_result': {
        const e = ev as {
          toolCallId?: string; name?: string; ok?: boolean; summary?: string;
          entities?: AiEntity[]; error?: string; durationMs?: number;
        };
        let call = stream.toolCalls.find((c) => c.id === e.toolCallId);
        if (!call) {
          call = { id: e.toolCallId || `${e.name}-${stream.toolCalls.length}`, name: e.name || 'tool', status: 'running' };
          stream.toolCalls.push(call);
        }
        call.ok = e.ok !== false;
        call.status = e.ok === false ? 'error' : 'done';
        call.summary = e.summary;
        call.error = e.error;
        call.durationMs = e.durationMs;
        if (Array.isArray(e.entities) && e.entities.length) {
          call.entities = e.entities;
          for (const ent of e.entities) {
            if (!stream.entities.some((x) => x.type === ent.type && String(x.id) === String(ent.id))) {
              stream.entities.push(ent);
            }
          }
        }
        return {};
      }
      case 'usage': {
        const { type: _t, ...usage } = ev as { type: string } & AiUsage;
        stream.usage = usage;
        return {};
      }
      case 'assistant_message': {
        const real = (ev as { message?: AiMessage; data?: AiMessage }).message ?? (ev as { data?: AiMessage }).data;
        return real ? { assistant: normalizeMessage(real) } : {};
      }
      case 'error': {
        const e = ev as { message?: string };
        stream.error = e.message || 'error';
        return {};
      }
      default:
        return {};
    }
  }

  /**
   * Send a message on the active conversation (creating one if needed) and stream the reply.
   * Returns the final assistant message (or null on failure/abort with no content).
   */
  async function sendMessage(content: string, opts: { type?: string } = {}): Promise<AiMessage | null> {
    const text = content.trim();
    if (!text || stream.active) return null;

    if (!activeConversation.value) {
      const conv = await createConversation(opts.type || 'chat');
      if (!conv) return null;
    }
    const conv = activeConversation.value!;

    errorMessage.value = '';
    lastFailedPrompt.value = '';
    resetStream();
    stream.active = true;
    stream.startedAt = Date.now();

    const tempUserId = `tmp-${Date.now()}`;
    messages.value.push({ id: tempUserId, conversation_id: conv.id, role: 'user', content: text, created_at: new Date().toISOString() });

    abortController = new AbortController();
    const controller = abortController;
    let finalAssistant: AiMessage | null = null;
    let aborted = false;

    try {
      const response = await fetch(`${api.defaults.baseURL}/ai/conversations/${conv.id}/messages/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'x-tenant-subdomain': localStorage.getItem('tenantSubdomain') || '',
          'Accept-Language': localStorage.getItem('locale') || 'en',
        },
        body: JSON.stringify({ content: text, providerSlug: selectedProvider.value || undefined }),
        signal: controller.signal,
      });

      if (!response.ok) {
        let detail = '';
        try {
          const body = await response.json();
          detail = body?.error?.message || body?.message || '';
        } catch {
          /* non-JSON body */
        }
        throw new Error(detail || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');
      const decoder = new TextDecoder();
      const parser = createSseLineParser();
      let done = false;

      const handlePayloads = (payloads: string[]) => {
        for (const payload of payloads) {
          const ev = parseSseData(payload);
          if (ev === 'done') {
            done = true;
            continue;
          }
          if (!ev) continue;
          const result = applyEvent(ev, tempUserId);
          if (result.assistant) finalAssistant = result.assistant;
        }
      };

      while (!done) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        handlePayloads(parser.push(decoder.decode(value, { stream: true })));
      }
      handlePayloads(parser.flush());
      if (done) {
        try {
          await reader.cancel();
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      const e = err as { name?: string; message?: string };
      if (e?.name === 'AbortError') {
        aborted = true;
      } else {
        stream.error = stream.error || e?.message || 'error';
      }
    } finally {
      if (abortController === controller) abortController = null;
    }

    // Finalize the assistant turn.
    const assistant = finalAssistant as AiMessage | null;
    if (assistant) {
      const merged: AiMessage = {
        ...assistant,
        metadata: {
          ...(assistant.metadata || {}),
          toolCalls: assistant.metadata?.toolCalls?.length ? assistant.metadata.toolCalls : stream.toolCalls.length ? [...stream.toolCalls] : undefined,
          entities: assistant.metadata?.entities?.length ? assistant.metadata.entities : stream.entities.length ? [...stream.entities] : undefined,
          usage: assistant.metadata?.usage ?? stream.usage ?? undefined,
        },
      };
      messages.value.push(merged);
      finalAssistant = merged;
    } else if (stream.content || stream.toolCalls.length) {
      const local: AiMessage = {
        id: `local-${Date.now()}`,
        conversation_id: conv.id,
        role: 'assistant',
        content: stream.content,
        created_at: new Date().toISOString(),
        metadata: {
          toolCalls: stream.toolCalls.length ? [...stream.toolCalls] : undefined,
          entities: stream.entities.length ? [...stream.entities] : undefined,
          usage: stream.usage ?? undefined,
          partial: aborted || !!stream.error,
        },
      };
      messages.value.push(local);
      finalAssistant = local;
    }

    if (stream.error && !aborted) {
      errorMessage.value = stream.error;
      if (!finalAssistant) {
        // Nothing came back: drop the optimistic user bubble and offer a retry.
        messages.value = messages.value.filter((m) => m.id !== tempUserId);
        lastFailedPrompt.value = text;
      }
    }

    resetStream();

    // Refresh the surrounding data without blocking the UI.
    void loadConversations();
    void loadConversationUsage(conv.id);
    refreshMeters();

    return finalAssistant;
  }

  async function retryLast(): Promise<AiMessage | null> {
    const text = lastFailedPrompt.value;
    if (!text) return null;
    lastFailedPrompt.value = '';
    errorMessage.value = '';
    return sendMessage(text);
  }

  /* ---------- Restore from URL ---------- */

  async function restoreFromRoute(): Promise<void> {
    const id = routeConversationId();
    if (id && activeConversation.value?.id !== id) {
      await openConversation(id);
    } else if (!id && activeConversation.value) {
      activeConversation.value = null;
      messages.value = [];
    }
  }

  return {
    // state
    loading,
    conversations,
    conversationsLoading,
    showArchived,
    searchQuery,
    activeConversation,
    messages,
    messagesLoading,
    providers,
    selectedProvider,
    currentProvider,
    credits,
    myUsage,
    usageLimits,
    conversationUsage,
    errorMessage,
    lastFailedPrompt,
    stream,
    // derived
    creditBalance,
    totalCredits,
    hasCreditCap,
    creditPercent,
    hasDailyLimit,
    messagesLeftToday,
    filteredConversations,
    pinnedConversations,
    unpinnedConversations,
    // actions
    loadAll,
    loadConversations,
    loadProviders,
    loadCredits,
    loadMyUsage,
    loadConversationUsage,
    refreshMeters,
    openConversation,
    goHome,
    createConversation,
    renameConversation,
    togglePin,
    toggleArchive,
    deleteConversation,
    sendMessage,
    stopStreaming,
    retryLast,
    restoreFromRoute,
    routeConversationId,
  };
}
